import {
  type Dinero,
  type DineroCurrency,
  dinero,
  add as dineroAdd,
  subtract as dineroSubtract,
  equal,
  toDecimal,
  toSnapshot,
} from "dinero.js";
import * as allCurrencies from "dinero.js/currencies";

export type Currency =
  (typeof allCurrencies)[keyof typeof allCurrencies]["code"];

const currencyMap: Record<string, DineroCurrency<number>> = {};
for (const obj of Object.values(allCurrencies)) {
  currencyMap[obj.code] = obj;
}

function isCurrency(value: string): value is Currency {
  return value in currencyMap;
}

export const CURRENCIES: ReadonlyArray<Currency> = Object.freeze(
  Object.keys(currencyMap).filter(isCurrency)
);

export function getCurrencyDigits(code: string): number | undefined {
  return currencyMap[code]?.exponent;
}

function toCurrencyObj(code: string): DineroCurrency<number> {
  const obj = currencyMap[code];
  if (!obj) {
    throw new TypeError(`Unknown currency ${code}`);
  }
  return obj;
}

interface SerializedAmount {
  amount: number;
  currency: Currency;
  precision: number;
}

export const AMOUNT_SAFE_MAX = 1_000_000_000_000_000;
const defaultLocale = "de-DE";

export class Amount {
  private dineroObj: Dinero<number>;

  private constructor(dineroObj: Dinero<number>) {
    this.dineroObj = dineroObj;
  }

  static fromAmountAndCurrency(amount: number, currency: string): Amount {
    if (Math.floor(amount) !== amount) {
      throw new TypeError(`Amount has to be an integer, got ${amount}`);
    }

    if (Math.abs(amount) > AMOUNT_SAFE_MAX) {
      throw new RangeError(
        `Amount out of range: ${amount}. Must be between -${AMOUNT_SAFE_MAX} and ${AMOUNT_SAFE_MAX}`
      );
    }
    const currencyObj = toCurrencyObj(currency);
    return new Amount(
      dinero({
        amount,
        currency: currencyObj,
        scale: currencyObj.exponent,
      })
    );
  }

  static fromDecimal(unit: number, currency: Currency): Amount {
    const currencyObj = toCurrencyObj(currency);
    const precision = currencyObj.exponent;
    const factor = 10 ** precision;
    const roundedAmount = Math.round(unit * factor);

    if (roundedAmount / factor !== unit) {
      throw new TypeError(
        `Unit ${unit} does not match precision ${precision} of currency ${currency}`
      );
    }

    return Amount.fromAmountAndCurrency(roundedAmount, currency);
  }

  static isAmount(value: unknown): value is Amount {
    return value instanceof Amount;
  }

  static serialise(amount: Amount) {
    return amount.toJSON();
  }

  static deserialise(obj: SerializedAmount): Amount {
    const currencyObj = toCurrencyObj(obj.currency);
    return new Amount(
      dinero({
        amount: obj.amount,
        currency: currencyObj,
        scale: obj.precision,
      })
    );
  }

  getDecimalAmount() {
    return Number(toDecimal(this.dineroObj));
  }

  getAmount() {
    return toSnapshot(this.dineroObj).amount;
  }

  getCurrency(): Currency {
    const code = toSnapshot(this.dineroObj).currency.code;
    // code is guaranteed to be a valid Currency since dinero.js maintains currency integrity
    if (!isCurrency(code)) {
      throw new TypeError(`Unexpected currency from dinero.js: ${code}`);
    }
    return code;
  }

  format() {
    const decimal = this.getDecimalAmount();
    const currency = this.getCurrency();
    return new Intl.NumberFormat(defaultLocale, {
      style: "currency",
      currency,
    }).format(decimal);
  }

  toLocaleString(locale: string) {
    const decimal = this.getDecimalAmount();
    const currency = this.getCurrency();
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(decimal);
  }

  toString() {
    return this.format();
  }

  toJSON(): SerializedAmount {
    const snapshot = toSnapshot(this.dineroObj);
    const code = snapshot.currency.code;
    if (!isCurrency(code)) {
      throw new TypeError(`Unexpected currency from dinero.js: ${code}`);
    }
    return {
      amount: snapshot.amount,
      currency: code,
      precision: snapshot.scale,
    };
  }

  add(amount: Amount): Amount {
    const result = dineroAdd(this.dineroObj, amount.dineroObj);
    return new Amount(result);
  }

  subtract(amount: Amount): Amount {
    const result = dineroSubtract(this.dineroObj, amount.dineroObj);
    return new Amount(result);
  }

  equals(amount: Amount): boolean {
    return equal(this.dineroObj, amount.dineroObj);
  }
}
